// src/services/businessCentralService.js - Fixed to avoid infinite loop
import { businessCentralScopes } from '../auth/authConfig';
import logger from '../utils/logger';

class BusinessCentralService {
  constructor(getAccessToken) {
    this.getAccessToken = getAccessToken;
    this.apiBaseUrl = process.env.REACT_APP_BC_API_BASE_URL || 
      'https://api.businesscentral.dynamics.com/v2.0/demotenant/api/v2.0';
    this.backendApiUrl = process.env.REACT_APP_API_BASE_URL || 'https://demobackend-backend-demo.uksouth-01.azurewebsites.net/api';
    this.activeEnvironment = null;
    this.companyId = null;
    this.environment = 'Production'; // Default environment - should be configurable
  }

  /**
   * Set active environment in instance properties
   * @param {string} environmentId - BC environment ID
   * @param {string} environmentName - BC environment name
   * @param {string} companyId - Optional company ID
   */
  updateEnvironmentState(environmentId, environmentName, companyId = null, company_Id = null) {
    this.activeEnvironment = { 
      id: environmentId, 
      name: environmentName 
    };
    
    // Handle both companyId and company_Id parameters 
    // (company_Id is from BC client tenant, companyId is legacy parameter)
    if (company_Id) {
      this.companyId = company_Id;
      localStorage.setItem('clientCompanyId', company_Id);
      logger.log(`Stored client company ID (company_Id): ${company_Id}`);
    } else if (companyId) {
      this.companyId = companyId;
      localStorage.setItem('clientCompanyId', companyId);
      logger.log(`Stored client company ID (companyId): ${companyId}`);
    }
    
    if (process.env.NODE_ENV === 'development') {
      logger.log(`Active environment set: ${environmentName} (${environmentId})`);
      logger.log(`Company ID: ${this.companyId || 'None'}`);
    }
    
    // Store in localStorage for persistence
    localStorage.setItem('activeEnvironment', JSON.stringify({
      id: environmentId,
      name: environmentName,
      companyId: this.companyId,
      company_Id: this.companyId // Store with both keys for backward compatibility
    }));
  }

  /**
   * Helper method to make authenticated API calls through our backend proxy
   * @param {string} endpoint - API endpoint path 
   * @param {object} options - Request options
   * @returns {Promise<object>} Response data
   */
  async fetchThroughBackend(endpoint, options = {}) {
    try {
      // For registration, we might not have the getAccessToken function
      let headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      
      // Always get a token for API calls - no bypasses allowed
      if (this.getAccessToken && options.scopes) {
        try {
          const token = await this.getAccessToken(options.scopes);
          if (!token || token === 'Not Readable' || token === 'mock-token-for-development') {
            throw new Error('Invalid or mock token received');
          }
          headers['Authorization'] = `Bearer ${token}`;
        } catch (error) {
          logger.error('Failed to get valid access token:', error?.message || error);
          throw new Error('Authentication failed. Please log in again.');
        }
      } else {
        throw new Error('Authentication service not available. Please log in again.');
      }

      // Merge any headers from options with our default headers
      if (options.headers) {
        headers = { ...headers, ...options.headers };
        // Remove headers from options to avoid duplication
        delete options.headers;
      }
      
      const defaultOptions = { headers };
      
      // Add environment context if available
      const enhancedOptions = {
        ...defaultOptions,
        ...options,
        credentials: 'include' // Critical: include credentials (cookies) with every request
      };
      
      if (this.activeEnvironment) {
        if (!enhancedOptions.headers) {
          enhancedOptions.headers = {};
        }
        enhancedOptions.headers['X-BC-Environment'] = this.activeEnvironment.id;
      }
      
      // Add registration ID from localStorage if available and not already included
      const registrationId = localStorage.getItem('registrationId');
      if (registrationId && !enhancedOptions.headers['X-Registration-ID']) {
        enhancedOptions.headers['X-Registration-ID'] = registrationId;
      }
      
      if (process.env.NODE_ENV === 'development') {
        logger.log(`Making API call to: ${endpoint}`);
      }

      const response = await fetch(
        `${this.backendApiUrl}${endpoint}`, 
        enhancedOptions
      );

      // Check for company ID in response headers and store it
      const clientCompanyId = response.headers.get('X-Client-Company-ID');
      if (clientCompanyId) {
        if (process.env.NODE_ENV === 'development') {
          logger.log(`Received client company ID from headers`);
        }
        localStorage.setItem('clientCompanyId', clientCompanyId);
      }

      // Handle non-ok responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || `API Error: ${response.status}`;
        throw {
          message: errorMessage,
          response: {
            status: response.status,
            data: errorData
          }
        };
      }

      return response.json();
    } catch (error) {
      logger.error(`Error in backend API call to ${endpoint}:`, error?.message || error);
      
      // In development mode, return mock data for certain endpoints
      if (process.env.NODE_ENV === 'development') {
        return this.getMockData(endpoint);
      }
      
      throw error;
    }
  }

  /**
   * Verify if the user exists in BC Portal Users
   * @param {string} email - User email
   * @param {string} userId - User ID/OID
   * @returns {Promise<object>} Verification result
   */
  async verifyPortalUser(email, userId) {
    try {
      if (process.env.NODE_ENV === 'development') {
        logger.log(`Requesting portal user verification`);
      }
      
      // Use the correct endpoint - portalUsers/verify
      const response = await this.fetchThroughBackend(`/bc/portalUsers/verify`, {
        method: 'POST',
        body: JSON.stringify({ 
          email, 
          userId 
        }),
        scopes: businessCentralScopes.read
      });

      if (process.env.NODE_ENV === 'development') {
        logger.log('Portal user verification successful');
      }

      // Store the registrationId in localStorage for potential use by other services
      if (response?.userData?.registrationId) {
        if (process.env.NODE_ENV === 'development') {
          logger.log('Storing registrationId in localStorage');
        }
        localStorage.setItem('registrationId', response.userData.registrationId);
      }

      return response;
    } catch (error) {
      logger.error('Error in portal user verification request:', error?.message || error);
      
      // Check if this is a "user not found" error (404)
      if (error.response && error.response.status === 404) {
        return {
          exists: false,
          message: error.response.data?.message || 'User is not registered in Business Central Portal'
        };
      }
      
      // For development mode, return mock data
      if (process.env.NODE_ENV === 'development') {
        logger.log('Development mode: Returning mock portal user data');
        return {
          exists: true,
          isActive: true,
          message: 'User found in Business Central Portal (mock)',
          userData: {
            id: 'mock-user-id',
            name: email.split('@')[0] || 'User',
            email: email,
            status: 'Active',
            registrationId: 'mock-registration-id',
            role: 'Admin',
            isAdmin: true
          }
        };
      }
      
      // Re-throw for other errors
      throw error;
    }
  }

  /**
   * Get employee profile from BC
   * @returns {Promise<object>} Employee profile
   */
  async getEmployeeProfile() {
    try {
      // Get the registration ID and client environment details from localStorage
      const registrationId = localStorage.getItem('registrationId');
      let clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;
      
      // Get current user details from localStorage to filter by email
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const userEmail = currentUser?.email;
      
      // If no clientCompanyId, check if we need to find it using the environment's companyName
      if (!clientCompanyId && activeEnvironment?.companyName) {
        logger.log(`No client company ID found, attempting to find exact match for company name: ${activeEnvironment.companyName}`);
        
        // Try to get client companies from localStorage
        const storedCompanies = localStorage.getItem('clientCompanies');
        if (storedCompanies) {
          try {
            const companies = JSON.parse(storedCompanies);
            
            if (Array.isArray(companies) && companies.length > 0) {
              // Use strict exact matching only - no fallback mechanism
              const matchingCompany = companies.find(company => {
                const envName = activeEnvironment.companyName.trim().toLowerCase();
                const companyName = (company.name || '').trim().toLowerCase();
                const displayName = (company.displayName || '').trim().toLowerCase();
                
                // Log comparison for debugging
                logger.log(`Comparing environment company "${envName}" with BC company "${companyName}" / displayName: "${displayName}"`);
                
                // Case-insensitive exact matches are accepted
                return companyName === envName || displayName === envName;
              });
              
              if (matchingCompany) {
                logger.log(`Found exact matching company for "${activeEnvironment.companyName}": ${matchingCompany.id}`);
                clientCompanyId = matchingCompany.id;
                localStorage.setItem('clientCompanyId', clientCompanyId);
                localStorage.setItem('clientCompanyName', matchingCompany.name || matchingCompany.displayName);
              } else {
                logger.warn(`No exact matching company found for "${activeEnvironment.companyName}"`);
                // No fallback - leave clientCompanyId unset if no exact match
                localStorage.removeItem('clientCompanyId');
                localStorage.removeItem('clientCompanyName');
                
                // Since we required an exact match and none was found, make sure clientCompanyId is null
                clientCompanyId = null;
              }
            }
          } catch (error) {
            logger.error('Error parsing stored companies:', error?.message || error);
          }
        }
      }
      
      // Check for required parameters
      if (!registrationId) {
        logger.warn('No registrationId found in localStorage, cannot fetch client tenant-specific employee data');
      } else {
        logger.log(`Using registrationId: ${registrationId} to fetch employee data from client tenant`);
      }
      
      if (!clientEnvironmentName) {
        logger.warn('No client environment name found in localStorage');
      } else {
        logger.log(`Using client environment: ${clientEnvironmentName}`);
      }
      
      if (!clientCompanyId) {
        logger.warn('No client company ID found in localStorage');
      } else {
        logger.log(`Using client company ID: ${clientCompanyId}`);
      }
      
      if (!userEmail) {
        logger.warn('No user email found in localStorage');
      } else {
        logger.log(`Filtering employee data by email: ${userEmail}`);
      }
      
      // Build the query parameters with all available client context
      const queryParams = new URLSearchParams();
      
      if (registrationId) {
        queryParams.append('registrationId', registrationId);
      }
      
      if (clientEnvironmentName) {
        queryParams.append('clientEnvironment', clientEnvironmentName);
      }
      
      if (clientCompanyId) {
        queryParams.append('clientCompanyId', clientCompanyId);
      }
      
      if (userEmail) {
        queryParams.append('email', userEmail.toLowerCase());
      }
      
      // Create the endpoint URL with query parameters
      const endpoint = `/bc/employees/current${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      logger.log(`Requesting employee data with endpoint: ${endpoint}`);
      
      // Make the API call, including all client details in headers as well
      const response = await this.fetchThroughBackend(endpoint, {
        method: 'GET',
        scopes: businessCentralScopes.read,
        headers: {
          'X-Registration-ID': registrationId || '',
          'X-Client-Environment': clientEnvironmentName || '',
          'X-Client-Company-ID': clientCompanyId || '',
          'X-User-Email': userEmail.toLowerCase() || ''
        }
      });
      
      // Store employee data in localStorage for use by other components
      localStorage.setItem('employeeNo', '');
      if (response && response.id) {
        localStorage.setItem('employeeId', response.id);
        localStorage.setItem('employeeData', JSON.stringify(response));
        
        // If employeeNo is available, store it for payslips API
        if (response.employeeNo) {
          localStorage.setItem('employeeNo', response.employeeNo);
          
          // Also update the session with the employee number
          try {
            if (process.env.NODE_ENV === 'development') {
              logger.log('Updating session with employeeNo after profile fetch');
            }
            
            // Get registration ID and user email from localStorage
            const registrationId = localStorage.getItem('registrationId');
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
            const userEmail = currentUser?.email;
            
            // Import ApiService and update the session
            const ApiService = require('../services/apiService').default;
            const apiService = new ApiService();
            
            // Prepare session update data
            const sessionData = {
              registrationId: registrationId || '',
              userEmail: userEmail.toLowerCase() || '',
              employeeNo: response.employeeNo
            };
            
            // Update the session with employee number
            const sessionResult = await apiService.initSession(sessionData);
            
            if (process.env.NODE_ENV === 'development') {
              logger.log('Session update with employeeNo result:', sessionResult);
            }
          } catch (sessionError) {
            logger.error('Error updating session with employeeNo:', sessionError);
            // Continue despite session update error
          }
        }
      }
      
      return response;
    } catch (error) {
      logger.error('Error fetching employee profile:', error?.message || error);
      
      // Return mock data for development/testing
      if (process.env.NODE_ENV === 'development') {
        const mockData = {
          id: "f1fe0e52-ec2e-f011-9a4a-002248415f33", // Added to match API example
          name: "John Smith",
          position: "Senior Software Engineer",
          department: "Engineering",
          employeeId: "EMP001",
          employeeNo: "1001", // Added to match API example
          email: "john.smith@example.com",
          joinDate: "2022-01-15",
          manager: "Jane Wilson",
          address: "123 Main Street",
          address2: "Apartment 4B",
          birthDate: "1990-05-20",
          gender: "Male",
          payFrequency: "Monthly",
          taxCode: "1150L",
          niNumber: "QQ123456C",
          niCategory: "A",
          hoursWorked: "37.5"
        };
        
        // Store mock data in localStorage for development
        localStorage.setItem('employeeId', mockData.id);
        localStorage.setItem('employeeData', JSON.stringify(mockData));
        localStorage.setItem('employeeNo', mockData.employeeNo);
        
        return mockData;
      }
      
      throw error;
    }
  }

  /**
   * Get payslips from BC
   * @returns {Promise<Array>} List of payslips
   */
  async getPayslips() {
    try {
      // Get the registration ID and client environment details from localStorage
      const registrationId = localStorage.getItem('registrationId');
      const clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;
      
      // Get current user details from localStorage to include email
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const userEmail = currentUser?.email;
      
      // Get employee profile data to get the employeeNo
      let employeeNo = '';
      try {
        employeeNo = localStorage.getItem('employeeNo') || '';
        if (!employeeNo) {
          logger.log('employeeNo not found in localStorage, will be fetched from employee profile');
        } else {
          logger.log(`Using employeeNo from localStorage: ${employeeNo}`);
        }
      } catch (profileError) {
        logger.error('Error getting employeeNo:', profileError);
      }
      
      // Log the parameters we're using
      logger.log('Fetching payslips with parameters:', {
        registrationId: registrationId || 'Not available',
        clientEnvironmentName: clientEnvironmentName || 'Not available',
        clientCompanyId: clientCompanyId || 'Not available',
        employeeNo: employeeNo || 'Not available',
        userEmail: userEmail ? userEmail.toLowerCase() : 'Not available'
      });

      // Get employeeNo from localStorage as a fallback
      const storedEmployeeNo = localStorage.getItem('employeeNo');

      // Set up the headers with all required parameters
      const headers = {
        'X-Registration-ID': registrationId || '',
        'X-Client-Environment': clientEnvironmentName || '',
        'X-Client-Company-ID': clientCompanyId || '',
        'X-Employee-No': employeeNo || '',
        'X-User-Email': userEmail ? userEmail.toLowerCase() : '',
        // Also include localStorage data as a fallback mechanism
        'X-LocalStorage-EmployeeNo': storedEmployeeNo || '',
        'X-Session-Request-Time': Date.now().toString()
      };
      
      // Make the API call with all parameters
      const response = await this.fetchThroughBackend('/bc/payslips', {
        method: 'GET',
        scopes: businessCentralScopes.read,
        headers: headers
      });
      
      // Return an empty array if response is not available or not an array
      return Array.isArray(response) ? response : [];
    } catch (error) {
      logger.error('Error fetching payslips:', error?.message || error);
      // Return an empty array on error instead of mock data
      return [];
    }
  }
  
  /**
   * Get payslip PDF for a specific payslip
   * @param {string} systemId - System ID of the payslip
   * @returns {Promise<string>} Base64 encoded PDF data
   */
  async getPayslipPdf(systemId) {
    try {
      if (!systemId) {
        throw new Error('Payslip systemId is required');
      }
      
      // Get the registration ID and client environment details from localStorage
      const registrationId = localStorage.getItem('registrationId');
      const clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;
      
      logger.log(`Fetching payslip PDF for systemId: ${systemId}`);
      
      // Set up the headers with all required parameters
      const headers = {
        'X-Registration-ID': registrationId || '',
        'X-Client-Environment': clientEnvironmentName || '',
        'X-Client-Company-ID': clientCompanyId || ''
      };
      
      // Make the API call with all parameters
      const response = await this.fetchThroughBackend(`/bc/payslips/${systemId}/pdf`, {
        method: 'POST',
        scopes: businessCentralScopes.read,
        headers: headers,
        body: JSON.stringify({ systemId })
      });
      
      if (response && response.value) {
        return response.value; // Return the base64 encoded PDF data
      } else {
        throw new Error('Invalid response format from payslip PDF API');
      }
    } catch (error) {
      logger.error(`Error fetching payslip PDF for systemId ${systemId}:`, error?.message || error);
      
      // For development, return a mock base64 PDF string
      if (process.env.NODE_ENV === 'development') {
        logger.log('Development mode: Returning mock PDF data');
        // This is a minimal valid PDF in base64
        return 'JVBERi0xLjcKJeLjz9MKNSAwIG9iago8PAovVHlwZSAvWE9iamVjdAovU3VidHlwZSAvSW1hZ2UKL1dpZHRoIDEyNDIKL0hlaWdodCAxNTMxCi9Db2xvclNwYWNlIFsvSW5kZXhlZCAvRGV2aWNlUkdCIDI1NSA8RkZGRkZGRkNDQ0NDQzMzMzMzMzY2NjY2Njk5OTk5OQ+XQovQml0c1BlckNvbXBvbmVudCA4Ci9GaWx0ZXIgL0ZsYXRlRGVjb2RlCj4+CmVuZG9iagozIDAgb2JqCjw8L1R5cGUgL1BhZ2UKL1BhcmVudCAyIDAgUgovUmVzb3VyY2VzIDQgMCBSCi9Db250ZW50cyA2IDAgUgo+PgplbmRvYmoKNiAwIG9iago8PC9MZW5ndGggNzA+PgpzdHJlYW0KcQoxIDAgMCAxIDAgMCBjbQovWDAgRG8KUQpxCkJUCi9GMSAxMiBUZgoxIDAgMCAxIDcwIDEwMCBUbQooTW9jayBQYXlzbGlwIFBERikgVGoKRVQKUQplbmRzdHJlYW0KZW5kb2JqCjQgMCBvYmoKPDwKL1Byb2NTZXQgWy9QREYgL1RleHQgL0ltYWdlQiAvSW1hZ2VDIC9JbWFnZUldCi9Gb250IDw8L0YxIDcgMCBSPj4KL1hPYmplY3QgPDwvWDAgNSAwIFI+Pgo+PgplbmRvYmoKNyAwIG9iago8PC9UeXBlIC9Gb250Ci9TdWJ0eXBlIC9UeXBlMQovQmFzZUZvbnQgL0hlbHZldGljYQo+PgplbmRvYmoKMiAwIG9iago8PAovVHlwZSAvUGFnZXMKL0tpZHMgWzMgMCBSXQovQ291bnQgMQo+PgplbmRvYmoKMSAwIG9iago8PAovVHlwZSAvQ2F0YWxvZwovUGFnZXMgMiAwIFIKPj4KZW5kb2JqCjggMCBvYmoKPDwKL1Byb2R1Y2VyIChQeXRob24gUERGTGliIHVzaW5nIFJlcG9ydExhYikKL0NyZWF0aW9uRGF0ZSAoRDoyMDIzMDUyMDE1MzAyOCkKPj4KZW5kb2JqCnhyZWYKMCA5CjAwMDAwMDAwMDAgNjU1MzUgZgowMDAwMDAwNjMxIDAwMDAwIG4KMDAwMDAwMDU3MiAwMDAwMCBuCjAwMDAwMDAxOTUgMDAwMDAgbgowMDAwMDAwMzYyIDAwMDAwIG4KMDAwMDAwMDAwMCAwMDAwMCBuCjAwMDAwMDAyNjIgMDAwMDAgbgowMDAwMDAwNTA2IDAwMDAwIG4KMDAwMDAwMDY4MCAwMDAwMCBuCnRyYWlsZXIKPDwKL1NpemUgOQovUm9vdCAxIDAgUgovSW5mbyA4IDAgUgo+PgpzdGFydHhyZWYKNzU5CiUlRU9GCg==';
      }
      
      throw error;
    }
  }
  
  /**
   * Format error message from BC API response by removing correlation ID
   * @param {object} error - Error object from API
   * @returns {string} Formatted error message
   */
  formatBCErrorMessage(error) {
    // Default error message if we can't extract a better one
    let errorMessage = 'An error occurred while processing your request';
    
    try {
      // Check if this is a BC API error with the specific format
      if (error.response?.data?.error?.message) {
        // Get the full error message
        const fullMessage = error.response.data.error.message;
        
        // Remove the correlation ID part (everything after "CorrelationId:")
        const messageParts = fullMessage.split('CorrelationId:');
        errorMessage = messageParts[0].trim();
        
        // Log the correlation ID for debugging but don't show to user
        if (messageParts.length > 1) {
          logger.log('Error correlation ID:', messageParts[1].trim());
        }
      } else if (error.response?.data?.error) {
        // If there's an error object but no message format we recognize
        errorMessage = error.response.data.error.code || 
                      error.response.data.error.toString();
      } else if (error.response?.data?.message) {
        // Simple message format
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Regular Error object
        errorMessage = error.message;
      }
      
      return errorMessage;
    } catch (e) {
      logger.error('Error formatting BC error message:', e);
      return errorMessage;
    }
  }

  /**
   * Get P45 document PDF for a specific employee
   * @param {string} employeeId - Employee ID
   * @returns {Promise<object>} Object with success status, data and error message if applicable
   */
  async getP45Pdf(employeeId) {
    try {
      if (!employeeId) {
        throw new Error('Employee ID is required');
      }
      
      // Get the registration ID and client environment details from localStorage
      const registrationId = localStorage.getItem('registrationId');
      const clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;
      
      logger.log(`Fetching P45 document for employee ID: ${employeeId}`);
      
      // Set up the headers with all required parameters
      const headers = {
        'X-Registration-ID': registrationId || '',
        'X-Client-Environment': clientEnvironmentName || '',
        'X-Client-Company-ID': clientCompanyId || ''
      };
      
      // Make the API call with all parameters
      const response = await this.fetchThroughBackend(`/bc/employees/${employeeId}/p45`, {
        method: 'POST',
        scopes: businessCentralScopes.read,
        headers: headers,
        body: JSON.stringify({ employeeId })
      });
      
      if (response && response.value) {
        return { 
          success: true,
          data: response.value,
          error: null
        };
      } else {
        throw new Error('Invalid response format from P45 document API');
      }
    } catch (error) {
      logger.error(`Error fetching P45 document for employee ID ${employeeId}:`, error?.message || error);
      
      // Get the formatted error message
      const errorMessage = this.formatBCErrorMessage(error);
      
      // In development mode, log the error but still return the error to the user
      if (process.env.NODE_ENV === 'development') {
        logger.log('Development mode: Not returning mock data, showing actual error to user');
      }
      
      // Return error information
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }
  
  /**
   * Get P60 document PDF for a specific employee
   * @param {string} employeeId - Employee ID
   * @param {string} taxYear - Optional tax year 
   * @returns {Promise<object>} Object with success status, data and error message if applicable
   */
  async getP60Pdf(employeeId, taxYear = null) {
    try {
      if (!employeeId) {
        throw new Error('Employee ID is required');
      }
      
      // Get the registration ID and client environment details from localStorage
      const registrationId = localStorage.getItem('registrationId');
      const clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;
      
      logger.log(`Fetching P60 document for employee ID: ${employeeId}${taxYear ? `, tax year: ${taxYear}` : ''}`);
      
      // Set up the headers with all required parameters
      const headers = {
        'X-Registration-ID': registrationId || '',
        'X-Client-Environment': clientEnvironmentName || '',
        'X-Client-Company-ID': clientCompanyId || ''
      };
      
      // Prepare request body
      const requestBody = {
        employeeId
      };
      
      // Add tax year if provided
      if (taxYear) {
        requestBody.taxYear = taxYear;
      }
      
      // Make the API call with all parameters
      const response = await this.fetchThroughBackend(`/bc/employees/${employeeId}/p60`, {
        method: 'POST',
        scopes: businessCentralScopes.read,
        headers: headers,
        body: JSON.stringify(requestBody)
      });
      
      if (response && response.value) {
        return {
          success: true,
          data: response.value,
          error: null
        };
      } else {
        throw new Error('Invalid response format from P60 document API');
      }
    } catch (error) {
      logger.error(`Error fetching P60 document for employee ID ${employeeId}:`, error?.message || error);
      
      // Get the formatted error message
      const errorMessage = this.formatBCErrorMessage(error);
      
      
      // Return error information
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }

  /**
   * Get fiscal years from client tenant
   * @returns {Promise<object>} Object with success status, data and error message if applicable
   */
  async getFiscalYears() {
    try {
      // Get the registration ID and client environment details from localStorage
      const registrationId = localStorage.getItem('registrationId');
      const clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;
      
      logger.log('Fetching fiscal years from client tenant');
      
      // Set up the headers with all required parameters
      const headers = {
        'X-Registration-ID': registrationId || '',
        'X-Client-Environment': clientEnvironmentName || '',
        'X-Client-Company-ID': clientCompanyId || ''
      };
      
      // Make the API call to get fiscal years
      const response = await this.fetchThroughBackend('/bc/fiscalyears', {
        method: 'GET',
        scopes: businessCentralScopes.read,
        headers: headers
      });
      
      if (response && Array.isArray(response)) {
        return {
          success: true,
          data: response,
          error: null
        };
      } else {
        throw new Error('Invalid response format from fiscal years API');
      }
    } catch (error) {
      logger.error('Error fetching fiscal years:', error?.message || error);
      
      // Get the formatted error message
      const errorMessage = this.formatBCErrorMessage(error);
      
      // Return error information
      return {
        success: false,
        data: [],
        error: errorMessage
      };
    }
  }

  /**
   * Get notifications for the logged-in employee (top 5, newest first)
   * @returns {Promise<Array>} List of notifications
   */
  async updatePortalUser(userId, userData) {
    try {
      const registrationId = localStorage.getItem('registrationId');
      const response = await this.fetchThroughBackend(`/bc/portalUsers/${userId}`, {
        method: 'PATCH',
        scopes: businessCentralScopes.readWrite,
        headers: { 'X-Registration-ID': registrationId || '' },
        body: JSON.stringify(userData)
      });
      return response;
    } catch (error) {
      logger.error('Error updating portal user:', error?.message || error);
      throw error;
    }
  }

  async markNotificationSeen(systemId) {
    try {
      const registrationId = localStorage.getItem('registrationId');
      const clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;

      const queryParams = new URLSearchParams();
      if (registrationId) queryParams.append('registrationId', registrationId);
      if (clientEnvironmentName) queryParams.append('clientEnvironment', clientEnvironmentName);
      if (clientCompanyId) queryParams.append('clientCompanyId', clientCompanyId);

      const endpoint = `/bc/notifications/${systemId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      await this.fetchThroughBackend(endpoint, {
        method: 'PATCH',
        scopes: businessCentralScopes.readWrite,
        body: JSON.stringify({ seen: true })
      });
    } catch (error) {
      logger.error('Error marking notification as seen:', error?.message || error);
    }
  }

  async getNotifications() {
    try {
      const registrationId = localStorage.getItem('registrationId');
      const clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;
      const employeeNo = localStorage.getItem('employeeNo');

      const queryParams = new URLSearchParams();
      if (registrationId) queryParams.append('registrationId', registrationId);
      if (clientEnvironmentName) queryParams.append('clientEnvironment', clientEnvironmentName);
      if (clientCompanyId) queryParams.append('clientCompanyId', clientCompanyId);
      if (employeeNo) queryParams.append('employeeNo', employeeNo);

      const endpoint = `/bc/notifications${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      logger.log(`Requesting notifications with endpoint: ${endpoint}`);

      const response = await this.fetchThroughBackend(endpoint, {
        method: 'GET',
        scopes: businessCentralScopes.read
      });

      return Array.isArray(response) ? response : [];
    } catch (error) {
      logger.error('Error fetching notifications:', error?.message || error);
      return [];
    }
  }

  /**
   * Get P11D document PDF for a specific employee
   * @param {string} employeeId - Employee ID
   * @param {string} taxYear - Optional tax year 
   * @returns {Promise<object>} Object with success status, data and error message if applicable
   */
  async getP11DPdf(employeeId, taxYear = null) {
    try {
      if (!employeeId) {
        throw new Error('Employee ID is required');
      }
      
      // Get the registration ID and client environment details from localStorage
      const registrationId = localStorage.getItem('registrationId');
      const clientCompanyId = localStorage.getItem('clientCompanyId');
      const activeEnvironment = JSON.parse(localStorage.getItem('activeEnvironment') || '{}');
      const clientEnvironmentName = activeEnvironment?.name;
      
      logger.log(`Fetching P11D document for employee ID: ${employeeId}${taxYear ? `, tax year: ${taxYear}` : ''}`);
      
      // Set up the headers with all required parameters
      const headers = {
        'X-Registration-ID': registrationId || '',
        'X-Client-Environment': clientEnvironmentName || '',
        'X-Client-Company-ID': clientCompanyId || ''
      };
      
      // Prepare request body
      const requestBody = {
        employeeId
      };
      
      // Add tax year if provided
      if (taxYear) {
        requestBody.taxYear = taxYear;
      }
      
      // Make the API call with all parameters
      const response = await this.fetchThroughBackend(`/bc/employees/${employeeId}/p11d`, {
        method: 'POST',
        scopes: businessCentralScopes.read,
        headers: headers,
        body: JSON.stringify(requestBody)
      });
      
      if (response && response.value) {
        return {
          success: true,
          data: response.value,
          error: null
        };
      } else {
        throw new Error('Invalid response format from P11D document API');
      }
    } catch (error) {
      logger.error(`Error fetching P11D document for employee ID ${employeeId}:`, error?.message || error);
      
      // Get the formatted error message
      const errorMessage = this.formatBCErrorMessage(error);
      
      
      // Return error information
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }

  /**
   * Update user status in Business Central
   * @param {string} userId - User ID
   * @param {string} newStatus - New status
   * @returns {Promise<object>} Update result
   */
  async updateUserStatus(userId, newStatus = 'Active') {
    logger.log(`BusinessCentralService: Updating user status for userId ${userId} to ${newStatus}`);
    
    try {
      // For development mode, return mock success
      if (process.env.NODE_ENV === 'development') {
        logger.log('Development mode: Mocking status update');
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        return {
          success: true,
          message: 'User status updated successfully (development mode)'
        };
      }
      
      const token = await this.getAccessToken(businessCentralScopes.read);
      if (!token || token === 'Not Readable' || token === 'mock-token-for-development') {
        throw new Error('Invalid authentication token. Please log in again.');
      }
      logger.log('BusinessCentralService: Got token for status update');
      
      // Full URL for the PATCH request
      const url = `${this.backendApiUrl}/bc/portalUsers/${userId}/updateStatus`;
      logger.log('BusinessCentralService: Sending request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      logger.log('BusinessCentralService: Status update response status:', response.status);
      
      if (!response.ok) {
        const result = await response.json();
        logger.error('BusinessCentralService: Non-OK response:', response.status, result);
        throw {
          message: result.message || `API Error: ${response.status}`,
          status: response.status,
          data: result
        };
      }
      
      return await response.json();
    } catch (error) {
      logger.error('BusinessCentralService: Error updating user status:', error?.message || error);
      
      // If in development mode and the real API call failed, return mock success
      if (process.env.NODE_ENV === 'development') {
        logger.log('Development mode: Returning mock success after error');
        return {
          success: true,
          message: 'User status updated successfully (mock after error)'
        };
      }
      
      throw error;
    }
  }

  /**
   * Get available environments
   * @returns {Promise<Array>} List of environments
   */
  async getAvailableEnvironments() {
    try {
      // Check if we have a registration ID
      const registrationId = localStorage.getItem('registrationId');
      let endpoint = '/bc/environments';
      
      if (registrationId) {
        // Add filter if registration ID is available - IMPORTANT: no apostrophes around the parameter value
        const filter = `$filter=registrationId eq ${encodeURIComponent(registrationId)}`;
        endpoint = `/bc/environments?${filter}`;
      }
      
      logger.log('getAvailableEnvironments, Fetching environments with registrationId:', registrationId);
      
      const response = await this.fetchThroughBackend(endpoint, {
        method: 'GET',
        scopes: businessCentralScopes.read
      });
      
      // Extract environments from response
      const environments = response.value || response.environments || [];
      
      // Transform to a consistent format
      return environments.map(env => ({
        id: env.id || env.environmentId,
        name: env.name || env.environmentName,
        type: env.type || env.environmentType || 'Production',
        url: env.url || env.environmentUrl || '',
        status: env.status || 'Active',
        companyName: env.companyName || env.company_name || ''
      }));
    } catch (error) {
      logger.error('Error fetching environments:', error?.message || error);
      
      // For development mode, return mock environments
      if (process.env.NODE_ENV === 'development') {
        return [
          {
            id: 'env-production-01',
            name: 'Production',
            type: 'Production',
            url: 'https://businesscentral.dynamics.com/production',
            status: 'Active'
          },
          {
            id: 'env-sandbox-01',
            name: 'Sandbox',
            type: 'Sandbox',
            url: 'https://businesscentral.dynamics.com/sandbox',
            status: 'Active'
          }
        ];
      }
      
      throw error;
    }
  }

  /**
   * Activate environment in the application
   * This function first updates the local state and then notifies the backend
   * if necessary, but DOES NOT call API endpoint if not needed
   * @param {string} environmentId - Environment ID 
   * @param {string} environmentName - Environment name
   * @param {string} companyId - Optional company ID
   * @param {boolean} updateBackend - Whether to notify backend
   * @returns {Promise<object>} Result
   */
  async setActiveEnvironment(environmentId, environmentName, companyId = null, updateBackend = false) {
    // Extract company_Id from environment if it's an object
    let company_Id = null;
    if (typeof environmentId === 'object' && environmentId !== null) {
      const environment = environmentId;
      environmentName = environment.name;
      company_Id = environment.company_Id;
      environmentId = environment.id;
    }
    
    // First update the local state with both companyId and company_Id
    this.updateEnvironmentState(environmentId, environmentName, companyId, company_Id);
    
    // Only call the backend if explicitly requested
    if (updateBackend) {
      try {
        // Skip the API call in development mode to avoid issues
        if (process.env.NODE_ENV === 'development') {
          logger.log('Development mode: Skipping backend activation API call');
          return {
            success: true,
            message: 'Environment activated successfully (development mode)'
          };
        }
        
        // Make the API call to notify backend
        const token = await this.getAccessToken(businessCentralScopes.read);
        if (!token || token === 'Not Readable' || token === 'mock-token-for-development') {
          throw new Error('Invalid authentication token. Please log in again.');
        }
        
        const response = await fetch(`${this.backendApiUrl}/bc/environments/activate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            environmentId,
            environmentName,
            companyId: this.companyId // Include company ID in the request
          })
        });
        
        if (!response.ok) {
          const error = await response.json();
          logger.warn('Backend environment activation failed but continuing anyway:', error);
        } else {
          const result = await response.json();
          logger.log('Backend environment activation result:', result);
        }
      } catch (error) {
        logger.error('Error in backend environment activation:', error?.message || error);
        // Continue despite backend error - the local state is already updated
      }
    }
    
    // Return success in any case since local state is updated
    return {
      success: true,
      environment: {
        id: environmentId,
        name: environmentName,
        companyId: this.companyId
      }
    };
  }

  /**
   * Get authorization status
   * @param {string} registrationId - Optional registration ID
   * @returns {Promise<object>} Authorization status
   */
  async getAuthorizationStatus(registrationId = null) {
    try {
      // Use registration ID from parameter or get from localStorage
      const regId = registrationId || localStorage.getItem('registrationId');
      
      // Create URL with registration ID if available
      let url = '/auth/status';
      if (regId) {
        url += `?registrationId=${encodeURIComponent(regId)}`;
      }
      
      const response = await this.fetchThroughBackend(url, {
        method: 'GET',
        scopes: businessCentralScopes.read
      });
      return response;
    } catch (error) {
      logger.error('Error fetching authorization status:', error?.message || error);
      
      // Return mock data for development/testing
      if (process.env.NODE_ENV === 'development') {
        // Generate a mock registration ID if not provided
        const mockRegId = registrationId || localStorage.getItem('registrationId') || '00000000-0000-0000-0000-000000000001';
        
        const mockAuthData = {
          isAuthorized: true,
          status: 'active',
          tokenType: 'Bearer',
          accessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
          refreshToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
          expiresAt: new Date(Date.now() + 86400000 * 87).toISOString(), // 87 days from now
          scope: 'https://api.businesscentral.dynamics.com/.default',
          clientId: 'demoClientID',
          tenantId: 'demoTenantID',
          issuedAt: new Date(Date.now() - 3600000 * 24 * 3).toISOString(), // 3 days ago
          environment: 'Production',
          registrationId: mockRegId
        };
        return mockAuthData;
      }
      
      throw error;
    }
  }
  
  /**
   * Get authorization URL
   * @param {string} registrationId - Optional registration ID
   * @returns {Promise<object>} Object containing authorization URL and registration ID
   */
  async getAuthorizationUrl(registrationId = null) {
    try {
      logger.log('==================== REQUESTING AUTHORIZATION URL ====================');
      logger.log(`Request started at: ${new Date().toISOString()}`);
      logger.log(`Authorization URL request timestamp: ${Date.now()}`);
      
      // Use registration ID from parameter or get from localStorage
      const regId = registrationId || localStorage.getItem('registrationId');
      logger.log(`Registration ID for auth URL: ${regId || 'Not provided'}`);
      
      // Create URL with registration ID if available
      let url = '/auth/bc-auth-url';
      if (regId) {
        url += `?registrationId=${encodeURIComponent(regId)}`;
        logger.log(`Using URL with registration ID: ${url}`);
      } else {
        logger.log(`Using URL without registration ID: ${url}`);
      }
      
      logger.log('Requesting auth URL from backend (with PKCE flow)');
      logger.log(`Backend API URL: ${this.backendApiUrl}${url}`);
      
      const response = await this.fetchThroughBackend(url, {
        method: 'GET',
        scopes: businessCentralScopes.read
      });
      
      logger.log('==================== AUTHORIZATION URL RESPONSE ====================');
      logger.log('Received auth URL from backend');
      logger.log(`Response received at: ${new Date().toISOString()}`);
      logger.log(`Registration ID in response: ${response.registrationId || 'Not provided'}`);
      logger.log(`PKCE enabled flag in response: ${response.pkceEnabled ? 'YES' : 'NO'}`);
      
      // Store this registration ID in localStorage right away
      if (response.registrationId) {
        logger.log(`Storing registration ID in localStorage: ${response.registrationId}`);
        localStorage.setItem('registrationId', response.registrationId);
      }
      
      // Verify the URL contains PKCE parameters and other required parts
      const hasPkce = response.authUrl.includes('code_challenge=');
      const hasScope = response.authUrl.includes('scope=');
      const hasRedirectUri = response.authUrl.includes('redirect_uri=');
      const hasResponseMode = response.authUrl.includes('response_mode=');
      const hasState = response.authUrl.includes('state=');
      
      logger.log('Auth URL validation:');
      logger.log(`- Has PKCE code_challenge: ${hasPkce ? 'YES' : 'NO'}`);
      logger.log(`- Has scope parameter: ${hasScope ? 'YES' : 'NO'}`);
      logger.log(`- Has redirect_uri parameter: ${hasRedirectUri ? 'YES' : 'NO'}`);
      logger.log(`- Has response_mode parameter: ${hasResponseMode ? 'YES' : 'NO'}`);
      logger.log(`- Has state parameter with registrationId: ${hasState ? 'YES' : 'NO'}`);
      
      if (!hasPkce) {
        logger.error('CRITICAL ERROR: Auth URL does not contain PKCE parameters - this will cause AADSTS9002325 errors!');
      } else {
        logger.log('Auth URL contains PKCE parameters - Authentication will use PKCE flow');
      }
      
      if (!hasScope || !hasRedirectUri || !hasResponseMode) {
        logger.error('WARNING: Auth URL is missing critical OAuth parameters');
      }
      
      if (!hasState && response.registrationId) {
        logger.warn('WARNING: Auth URL does not contain state parameter with registration ID');
      }
      
      logger.log('====================================================================');
      
      // Return both the auth URL and registration ID
      return {
        authUrl: response.authUrl,
        registrationId: response.registrationId
      };
    } catch (error) {
      logger.error('Error getting authorization URL:', error?.message || error);
      
      // Return mock URL for development
      if (process.env.NODE_ENV === 'development') {
        // Generate a mock registration ID if not provided
        const mockRegId = registrationId || localStorage.getItem('registrationId') || '00000000-0000-0000-0000-000000000001';
        
        logger.log('Development mode: Returning mock authorization URL');
        logger.log(`Using mock registration ID: ${mockRegId}`);
        
        const mockAuthUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=mock-client-id&response_type=code&redirect_uri=http://localhost:3000/auth/callback&response_mode=query&scope=https://api.businesscentral.dynamics.com/.default&prompt=consent&state=${encodeURIComponent(JSON.stringify({ registrationId: mockRegId }))}`;
        
        return {
          authUrl: mockAuthUrl,
          registrationId: mockRegId
        };
      }
      
      throw error;
    }
  }
  
  /**
   * Refresh authorization token
   * @param {string} registrationId - Optional registration ID
   * @returns {Promise<object>} Refresh result
   */
  async refreshAuthorizationToken(registrationId = null) {
    try {
      // Use registration ID from parameter or get from localStorage
      const regId = registrationId || localStorage.getItem('registrationId');
      
      // Create request body with registration ID if available
      const requestData = regId ? { registrationId: regId } : {};
      
      const response = await this.fetchThroughBackend('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify(requestData),
        scopes: businessCentralScopes.read
      });
      return response;
    } catch (error) {
      logger.error('Error refreshing authorization token:', error?.message || error);
      
      // Return mock data for development
      if (process.env.NODE_ENV === 'development') {
        // Generate a mock registration ID if not provided
        const mockRegId = registrationId || localStorage.getItem('registrationId') || '00000000-0000-0000-0000-000000000001';
        
        return {
          accessToken: 'new-mock-access-token',
          refreshToken: 'new-mock-refresh-token',
          expiresAt: new Date(Date.now() + 86400000 * 90).toISOString(),
          issuedAt: new Date().toISOString(),
          registrationId: mockRegId
        };
      }
      
      throw error;
    }
  }
  
  /**
   * Store authorization tokens in Business Central
   * @param {object} tokenData - Token data including access_token, refresh_token, expires_in
   * @param {string} registrationId - Optional registration ID
   * @returns {Promise<boolean>} Success indicator
   */
  async storeTokensInBusinessCentral(tokenData, registrationId = null) {
    try {
      if (process.env.NODE_ENV === 'development') {
        logger.log('Storing tokens in Business Central...');
      }
      
      // Use registration ID from parameter or get from localStorage or tokenData
      const regId = registrationId || 
                   tokenData.registrationId || 
                   localStorage.getItem('registrationId');
      
      if (!regId) {
        throw new Error('Registration ID is required for storing authorization tokens');
      }
      
      // Extract token information
      const { 
        accessToken, 
        refreshToken, 
        expiresAt, 
        expiresIn = 3600, 
        clientId, 
        tenantId, 
        environment = 'Production' 
      } = tokenData;
      
      // In development mode, return mock success
      if (process.env.NODE_ENV === 'development') {
        logger.log(`Development mode: Mock storing tokens in BC for registration ID: ${regId}`);
        return {
          success: true,
          message: `Tokens stored in Business Central (mock) for registration ID: ${regId}`,
          registrationId: regId
        };
      }
      
      // Call the BC API endpoint to store tokens
      const response = await this.fetchThroughBackend('/bc/authorization/storeTokens', {
        method: 'POST',
        body: JSON.stringify({
          registrationId: regId,
          accessToken,
          refreshToken,
          expiresInSeconds: expiresIn,
          clientId,
          tenantId,
          environment
        }),
        scopes: businessCentralScopes.readWrite
      });
      
      if (process.env.NODE_ENV === 'development') {
        logger.log('Business Central token storage successful');
      }
      return response;
    } catch (error) {
      logger.error('Error storing tokens in Business Central:', error?.message || error);
      
      // Return mock success in development mode despite error
      if (process.env.NODE_ENV === 'development') {
        const mockRegId = registrationId || 
                         tokenData.registrationId || 
                         localStorage.getItem('registrationId') || 
                         '00000000-0000-0000-0000-000000000001';
        
        return {
          success: true,
          message: `Tokens stored in Business Central (mock after error) for registration ID: ${mockRegId}`,
          registrationId: mockRegId
        };
      }
      
      throw error;
    }
  }
  
  /**
   * Get portal users
   * @returns {Promise<Array>} List of portal users
   */
  async getPortalUsers() {
    try {
      const response = await this.fetchThroughBackend('/bc/portalUsers', {
        method: 'GET',
        scopes: businessCentralScopes.read
      });
      return response.value || [];
    } catch (error) {
      logger.error('Error fetching portal users:', error?.message || error);
      
      // Return mock data for development
      if (process.env.NODE_ENV === 'development') {
        return [
          { 
            id: '1', 
            name: 'John Smith', 
            email: 'john.smith@example.com', 
            status: 'Active',
            role: 'User',
            isAdmin: false,
            lastLogin: '2025-05-05T12:30:00Z',
            created: '2025-01-15T09:00:00Z'
          },
          { 
            id: '2', 
            name: 'Emma Thompson', 
            email: 'emma.thompson@example.com', 
            status: 'Active',
            role: 'User',
            isAdmin: false,
            lastLogin: '2025-05-04T15:45:00Z',
            created: '2025-01-20T10:15:00Z'
          },
          { 
            id: '3', 
            name: 'Michael Johnson', 
            email: 'michael.johnson@example.com', 
            status: 'PendingFirstLogin',
            role: 'User',
            isAdmin: false,
            lastLogin: null,
            created: '2025-05-01T11:30:00Z'
          },
          { 
            id: '4', 
            name: 'Sarah Williams', 
            email: 'sarah.williams@example.com', 
            status: 'Inactive',
            role: 'User',
            isAdmin: false,
            lastLogin: '2025-03-15T14:20:00Z',
            created: '2025-01-25T08:45:00Z'
          },
          { 
            id: '5', 
            name: 'Admin User', 
            email: 'admin@example.com', 
            status: 'Active',
            role: 'Admin',
            isAdmin: true,
            lastLogin: '2025-05-08T09:10:00Z',
            created: '2025-01-10T08:00:00Z'
          }
        ];
      }
      
      throw error;
    }
  }
  
  /**
   * Create a new portal user
   * @param {object} userData - User data
   * @returns {Promise<object>} Created user
   */
  async createPortalUser(userData) {
    try {
      const response = await this.fetchThroughBackend('/bc/portalUsers', {
        method: 'POST',
        body: JSON.stringify(userData),
        scopes: businessCentralScopes.readWrite
      });
      return response;
    } catch (error) {
      logger.error('Error creating portal user:', error?.message || error);
      
      // Return mock data for development
      if (process.env.NODE_ENV === 'development') {
        return {
          id: `new-${Date.now()}`,
          ...userData,
          status: 'PendingFirstLogin',
          created: new Date().toISOString(),
          lastLogin: null
        };
      }
      
      throw error;
    }
  }
  
  /**
   * Create a new environment
   * @param {object} environmentData - Environment data
   * @returns {Promise<object>} Created environment
   */
  async createEnvironment(environmentData) {
    try {
      const response = await this.fetchThroughBackend('/bc/environments', {
        method: 'POST',
        body: JSON.stringify(environmentData),
        scopes: businessCentralScopes.readWrite
      });
      return response;
    } catch (error) {
      logger.error('Error creating environment:', error?.message || error);
      
      // Return mock data for development
      if (process.env.NODE_ENV === 'development') {
        return {
          id: `env-${Date.now()}`,
          ...environmentData,
          status: 'Active',
          lastSync: new Date().toISOString(),
          version: '23.0.13234.0'
        };
      }
      
      throw error;
    }
  }
  
  /**
   * Get tenant information
   * @returns {Promise<object>} Tenant information
   */
  async getTenantInfo() {
    try {
      const response = await this.fetchThroughBackend('/bc/portalRegistrations', {
        method: 'GET',
        scopes: businessCentralScopes.read
      });
      return response;
    } catch (error) {
      logger.error('Error fetching tenant info:', error?.message || error);
      
      // Return mock data for development
      if (process.env.NODE_ENV === 'development') {
        return {
          name: 'Demo Company Ltd',
          id: 'tenant-123',
          registrationDate: '2025-01-15'
        };
      }
      
      throw error;
    }
  }
  
  /**
   * Get client tenant companies
   * @param {string} environmentName - Environment name
   * @param {string} registrationId - Registration ID
   * @returns {Promise<Array>} List of companies
   */
  async getClientTenantCompanies(environmentName, registrationId) {
    try {
      if (!environmentName) {
        throw new Error('Environment name is required');
      }
      
      if (!registrationId) {
        registrationId = localStorage.getItem('registrationId');
        if (!registrationId) {
          throw new Error('Registration ID is required');
        }
      }
      
      logger.log(`Getting client tenant companies for environment: ${environmentName}, registrationId: ${registrationId}`);
      
      const response = await this.fetchThroughBackend('/bc/client-tenant/companies', {
        method: 'POST',
        body: JSON.stringify({
          environmentName,
          registrationId
        }),
        scopes: businessCentralScopes.read
      });
      
      // Extract companies from the response
      const companies = response.value || response || [];
      
      logger.log(`Retrieved ${companies.length} companies from client tenant`);
      
      return companies;
    } catch (error) {
      logger.error('Error fetching client tenant companies:', error?.message || error);
      
      // For development mode, return mock data
      if (process.env.NODE_ENV === 'development') {
        logger.log('Development mode: Returning mock client tenant companies');
        
        return [
          {
            id: 'cmp-001',
            name: 'CRONUS UK Ltd.',
            displayName: 'CRONUS UK Ltd.',
            businessProfileId: 'prof-001',
            systemVersion: '22.0.54157.55220'
          },
          {
            id: 'cmp-002',
            name: 'Demo Company',
            displayName: 'Demo Company',
            businessProfileId: 'prof-002',
            systemVersion: '22.0.54157.55220'
          }
        ];
      }
      
      throw error;
    }
  }

  /**
   * Get mock data for development
   * @param {string} endpoint - API endpoint
   * @returns {object} Mock data
   */
  getMockData(endpoint) {
    // Simple mock data provider
    if (endpoint.includes('/bc/portalUsers/verify')) {
      return {
        exists: true,
        isActive: true,
        message: 'User found in Business Central Portal (mock)',
        userData: {
          id: 'mock-user-id',
          name: 'Test User',
          email: 'test.user@example.com',
          status: 'Active',
          registrationId: 'mock-registration-id',
          role: 'Employee'
        }
      };
    }
    
    if (endpoint.includes('/bc/client-tenant/companies')) {
      return {
        value: [
          {
            id: 'cmp-001',
            name: 'CRONUS UK Ltd.',
            displayName: 'CRONUS UK Ltd.',
            businessProfileId: 'prof-001',
            systemVersion: '22.0.54157.55220'
          },
          {
            id: 'cmp-002',
            name: 'Demo Company',
            displayName: 'Demo Company',
            businessProfileId: 'prof-002',
            systemVersion: '22.0.54157.55220'
          }
        ]
      };
    }
    
    if (endpoint.includes('/bc/employees/current')) {
      // Extract registration ID from endpoint if available
      const registrationIdMatch = endpoint.match(/registrationId=([^&]+)/);
      const registrationId = registrationIdMatch ? decodeURIComponent(registrationIdMatch[1]) : 
                             localStorage.getItem('registrationId');
      
      logger.log(`Mock data for employee endpoint with registrationId: ${registrationId || 'Not available'}`);
      
      // Return mock data with some customization based on registration ID if available
      return {
        name: registrationId ? `Employee from ${registrationId.substring(0, 8)}` : "John Smith",
        position: "Senior Software Engineer",
        department: "Engineering",
        employeeId: registrationId ? `EMP-${registrationId.substring(0, 6)}` : "EMP001",
        email: "john.smith@example.com",
        joinDate: "2022-01-15",
        manager: "Jane Wilson",
        address: "123 Main Street",
        address2: "Apartment 4B",
        birthDate: "1990-05-20",
        gender: "Male",
        payFrequency: "Monthly",
        taxCode: "1150L",
        niNumber: "QQ123456C",
        niCategory: "A",
        hoursWorked: "37.5",
        // Add a flag to indicate this is mock data
        _isMockData: true
      };
    }
    
    if (endpoint.includes('/bc/environments')) {
      return {
        value: [
          {
            id: 'env-production-01',
            name: 'Production',
            type: 'Production',
            url: 'https://businesscentral.dynamics.com/production',
            status: 'Active'
          },
          {
            id: 'env-sandbox-01',
            name: 'Sandbox',
            type: 'Sandbox',
            url: 'https://businesscentral.dynamics.com/sandbox',
            status: 'Active'
          }
        ]
      };
    }
    
    // Default mock data
    return { 
      success: true, 
      message: 'Mock data for ' + endpoint 
    };
  }
}

export default BusinessCentralService;